import type { CaseNotebook, HistoryEntry, RecheckOutcome, Sensitivity, SourceReceipt, SourceRelation, TimePrecision, UserClaim, VaultData } from './models';

const id = (prefix: string) => `${prefix}_${crypto.randomUUID()}`;
const now = () => new Date().toISOString();

export function appendHistory(vault: VaultData, entry: Omit<HistoryEntry, 'id' | 'at'>): VaultData {
  const at = now();
  return { ...vault, updatedAt: at, history: [...vault.history, { ...entry, id: id('history'), at }] };
}

export function createCase(vault: VaultData, label: string, sensitivity: Sensitivity): VaultData {
  const cleaned = label.trim();
  if (cleaned.length < 2 || cleaned.length > 80) throw new Error('CASE_LABEL_INVALID');
  const at = now();
  const notebook: CaseNotebook = {
    id: id('case'), label: cleaned, sensitivity, createdAt: at, updatedAt: at, receipts: [], claims: [], revision: 1,
  };
  return appendHistory({ ...vault, cases: [notebook, ...vault.cases] }, {
    caseId: notebook.id, type: 'case_created', summary: '기사 묶음을 만들었어요.', revision: 1,
  });
}

export function normalizeSourceUrl(raw: string, stripQuery = true): string {
  const parsed = new URL(raw.trim());
  if (!['http:', 'https:'].includes(parsed.protocol) || parsed.username || parsed.password) throw new Error('UNSAFE_URL');
  parsed.hash = '';
  if (stripQuery) {
    const trackingKeys = new Set([
      'fbclid', 'gclid', 'dclid', 'msclkid', 'mc_cid', 'mc_eid',
      'ref_src', 'ref_url', 'igshid', 'vero_conv', 'vero_id',
    ]);
    for (const key of [...parsed.searchParams.keys()]) {
      const normalized = key.toLowerCase();
      if (normalized.startsWith('utm_') || trackingKeys.has(normalized)) parsed.searchParams.delete(key);
    }
  }
  return parsed.toString();
}

export interface ReceiptInput {
  url: string;
  publisher: string;
  publishedAt?: string;
  modifiedAt?: string;
  officialCorrectionUrl?: string;
  relation: SourceRelation;
  relatedReceiptId?: string;
  timePrecision: TimePrecision;
  stripQuery?: boolean;
  archiveUrl?: string;
}

/**
 * Keep the saved receipt array append-only, while rendering a stable chronological
 * comparison.  A source whose publication time was not confirmed belongs after
 * dated sources; its original confirmation order is still deterministic.
 */
export function sortReceiptsForTimeline(receipts: SourceReceipt[]): SourceReceipt[] {
  const timestamp = (receipt: SourceReceipt) => {
    if (receipt.timePrecision === 'unknown' || !receipt.publishedAt) return undefined;
    const value = Date.parse(receipt.publishedAt);
    return Number.isFinite(value) ? value : undefined;
  };
  return [...receipts].sort((left, right) => {
    const leftTime = timestamp(left);
    const rightTime = timestamp(right);
    if (leftTime !== undefined && rightTime !== undefined && leftTime !== rightTime) return leftTime - rightTime;
    if (leftTime !== undefined && rightTime === undefined) return -1;
    if (leftTime === undefined && rightTime !== undefined) return 1;
    return Date.parse(left.checkedAt) - Date.parse(right.checkedAt) || left.revision - right.revision;
  });
}

export function addReceipt(vault: VaultData, caseId: string, input: ReceiptInput): VaultData {
  const notebook = vault.cases.find((item) => item.id === caseId);
  if (!notebook) throw new Error('CASE_NOT_FOUND');
  const publisher = input.publisher.trim();
  if (!publisher || publisher.length > 80) throw new Error('PUBLISHER_INVALID');
  const url = normalizeSourceUrl(input.url, input.stripQuery ?? true);
  const correction = input.officialCorrectionUrl?.trim()
    ? normalizeSourceUrl(input.officialCorrectionUrl, input.stripQuery ?? true)
    : undefined;
  if (input.relatedReceiptId && !notebook.receipts.some((receipt) => receipt.id === input.relatedReceiptId)) {
    throw new Error('CROSS_CASE_RELATION_DENIED');
  }
  const at = now();
  const receipt: SourceReceipt = {
    id: id('receipt'), url, publisher, publishedAt: input.publishedAt || undefined,
    modifiedAt: input.modifiedAt || undefined, officialCorrectionUrl: correction, archiveUrl:input.archiveUrl?validateArchiveUrl(input.archiveUrl):undefined,
    checkedAt: at, relation: input.relation, relatedReceiptId: input.relatedReceiptId || undefined,
    timePrecision: input.timePrecision, createdAt: at, revision: notebook.revision + 1,
  };
  const cases = vault.cases.map((item) => item.id === caseId
    ? { ...item, receipts: [...item.receipts, receipt], updatedAt: at, revision: item.revision + 1 }
    : item);
  return appendHistory({ ...vault, cases, verificationQueue: (vault.verificationQueue ?? []).filter(item => item.caseId !== caseId || normalizeSourceUrl(item.url, input.stripQuery ?? true) !== url) }, {
    caseId, type: 'receipt_added', summary: `${publisher} 기사 기록을 추가했어요.`, revision: receipt.revision,
  });
}

export function addClaim(vault: VaultData, caseId: string, text: string, sourceReceiptIds: string[]): VaultData {
  const notebook = vault.cases.find((item) => item.id === caseId);
  if (!notebook) throw new Error('CASE_NOT_FOUND');
  const cleaned = text.trim();
  if (cleaned.length < 2 || cleaned.length > 500) throw new Error('CLAIM_TEXT_INVALID');
  if (!sourceReceiptIds.length || sourceReceiptIds.some((receiptId) => !notebook.receipts.some((receipt) => receipt.id === receiptId))) {
    throw new Error('CLAIM_SOURCE_REQUIRED');
  }
  const at = now();
  const claim: UserClaim = { id: id('claim'), text: cleaned, sourceReceiptIds, createdAt: at, revision: notebook.revision + 1 };
  const cases = vault.cases.map((item) => item.id === caseId
    ? { ...item, claims: [...item.claims, claim], updatedAt: at, revision: item.revision + 1 }
    : item);
  return appendHistory({ ...vault, cases }, {
    caseId, type: 'claim_added', summary: '사용자 작성 주장을 출처에 연결했어요.', revision: claim.revision,
  });
}

export function setReminder(vault: VaultData, caseId: string, reminderAt?: string): VaultData {
  const notebook = vault.cases.find((item) => item.id === caseId);
  if (!notebook) throw new Error('CASE_NOT_FOUND');
  const at = now();
  const revision = notebook.revision + 1;
  const cases = vault.cases.map((item) => item.id === caseId ? { ...item, reminderAt, updatedAt: at, revision } : item);
  return appendHistory({ ...vault, cases }, {
    caseId, type: 'reminder_changed', summary: reminderAt ? '다시 확인할 날짜를 정했어요.' : '다시 확인 알림을 멈췄어요.', revision,
  });
}

export function archiveCase(vault: VaultData, caseId: string, restore = false): VaultData {
  const notebook = vault.cases.find((item) => item.id === caseId);
  if (!notebook) throw new Error('CASE_NOT_FOUND');
  const at = now();
  const revision = notebook.revision + 1;
  const cases = vault.cases.map((item) => item.id === caseId
    ? { ...item, archivedAt: restore ? undefined : at, updatedAt: at, revision }
    : item);
  return appendHistory({ ...vault, cases }, {
    caseId, type: restore ? 'case_restored' : 'case_archived', summary: restore ? '기사 묶음을 복원했어요.' : '기사 묶음을 보관했어요.', revision,
  });
}

export function dueCases(vault: VaultData, at = Date.now()): CaseNotebook[] {
  return vault.cases.filter((item) => !item.archivedAt && item.reminderAt && new Date(item.reminderAt).getTime() <= at);
}

export function enqueueVerification(vault: VaultData, caseId: string, rawUrl: string, note = ''): VaultData {
  const notebook = vault.cases.find((item) => item.id === caseId);
  if (!notebook) throw new Error('CASE_NOT_FOUND');
  const url = normalizeSourceUrl(rawUrl, false);
  const cleaned = note.trim();
  if (cleaned.length > 200) throw new Error('QUEUE_NOTE_INVALID');
  const at = now();
  const item = { id: id('queue'), caseId, url, note: cleaned, createdAt: at };
  return appendHistory({ ...vault, verificationQueue: [...(vault.verificationQueue ?? []), item] }, {
    caseId, type: 'verification_queued', summary: '직접 확인할 출처를 대기열에 넣었어요.', revision: notebook.revision,
  });
}

export function removeVerification(vault: VaultData, queueId: string): VaultData {
  const item = (vault.verificationQueue ?? []).find((row) => row.id === queueId);
  if (!item) throw new Error('QUEUE_ITEM_NOT_FOUND');
  const notebook = vault.cases.find((row) => row.id === item.caseId);
  return appendHistory({ ...vault, verificationQueue: (vault.verificationQueue ?? []).filter((row) => row.id !== queueId) }, {
    caseId: item.caseId, type: 'verification_removed', summary: '출처 확인 대기열에서 항목을 치웠어요.', revision: notebook?.revision ?? 1,
  });
}

export function recordRecheck(vault: VaultData, caseId: string, outcome: RecheckOutcome, note = '', checkedAt = now()): VaultData {
  const notebook = vault.cases.find((item) => item.id === caseId);
  if (!notebook) throw new Error('CASE_NOT_FOUND');
  const cleaned = note.trim();
  if (cleaned.length > 300) throw new Error('RECHECK_NOTE_INVALID');
  const entry = { id: id('recheck'), caseId, outcome, note: cleaned, checkedAt };
  const cases = vault.cases.map((item) => item.id === caseId
    ? { ...item, reminderAt: outcome === 'check_later' ? new Date(Date.parse(checkedAt)+86400000).toISOString() : undefined, updatedAt: checkedAt, revision: item.revision + 1 }
    : item);
  return appendHistory({ ...vault, cases, rechecks: [...(vault.rechecks ?? []), entry] }, {
    caseId, type: 'recheck_recorded', summary: '사용자가 다시 확인한 결과를 기록했어요.', revision: notebook.revision + 1,
  });
}

function changeCase(vault: VaultData, caseId: string, type: HistoryEntry['type'], change: (item: CaseNotebook) => CaseNotebook, before?: Record<string, string>, after?:Record<string,string>): VaultData {
  const item = vault.cases.find(row => row.id === caseId);
  if (!item) throw new Error('CASE_NOT_FOUND');
  const revision = item.revision + 1;
  const next = { ...change(structuredClone(item)), revision, updatedAt: now() };
  return appendHistory({ ...vault, cases: vault.cases.map(row => row.id === caseId ? next : row) }, { caseId, type, revision, after, summary: ({receipt_edited:'기사 기록을 고쳤어요.',receipt_removed:'기사 기록을 지웠어요.',receipt_moved:'기사를 옮겼어요.',claim_edited:'메모를 고쳤어요.',claim_removed:'메모를 지웠어요.',case_renamed:'묶음 이름과 목적을 바꿨어요.'} as Partial<Record<HistoryEntry['type'],string>>)[type] ?? '기록을 바꿨어요.', before });
}
export function validateArchiveUrl(raw: string): string | undefined {
  if (!raw.trim()) return undefined;
  const value = normalizeSourceUrl(raw);
  if (!['web.archive.org','archive.ph','archive.today','archive.is','archive.md'].includes(new URL(value).hostname)) throw new Error('ARCHIVE_URL_INVALID');
  return value;
}
export function editReceipt(vault: VaultData, caseId: string, receiptId: string, patch: ReceiptInput): VaultData {
  const item = vault.cases.find(row => row.id === caseId)?.receipts.find(row => row.id === receiptId);
  if (!item) throw new Error('RECEIPT_NOT_FOUND');
  if (patch.relatedReceiptId === receiptId) throw new Error('SELF_RELATION_DENIED');
  const validated = addReceipt(vault, caseId, patch).cases.find(row => row.id === caseId)!.receipts.at(-1)!;
  const before = Object.fromEntries(Object.entries(item).filter(([key,value]) => typeof value === 'string' && key in patch)) as Record<string,string>;
  return changeCase(vault, caseId, 'receipt_edited', notebook => ({...notebook, receipts: notebook.receipts.map(row => row.id === receiptId ? {...validated, id: item.id, checkedAt:item.checkedAt, createdAt:item.createdAt, archiveUrl:validateArchiveUrl(patch.archiveUrl ?? ''), editedAt:now()} : row)}), before, Object.fromEntries(Object.entries(patch).filter(([,value])=>typeof value==='string')) as Record<string,string>);
}
export function removeReceipt(vault: VaultData, caseId: string, receiptId: string): VaultData {
  return changeCase(vault,caseId,'receipt_removed', item => ({...item, receipts:item.receipts.filter(row=>row.id!==receiptId).map(row=>row.relatedReceiptId===receiptId?{...row,relatedReceiptId:undefined}:row), claims:item.claims.map(row=>({...row, sourceReceiptIds:row.sourceReceiptIds.filter(value=>value!==receiptId)})).filter(row=>row.sourceReceiptIds.length>0)}));
}
export function editClaim(vault: VaultData, caseId: string, claimId: string, text: string, ids: string[]): VaultData {
  const item = vault.cases.find(row=>row.id===caseId)?.claims.find(row=>row.id===claimId);
  if (!item) throw new Error('CLAIM_NOT_FOUND');
  const valid = addClaim(vault,caseId,text,ids).cases.find(row=>row.id===caseId)!.claims.at(-1)!;
  return changeCase(vault,caseId,'claim_edited', notebook=>({...notebook, claims:notebook.claims.map(row=>row.id===claimId?{...valid,id:claimId,createdAt:item.createdAt,editedAt:now()}:row)}),{text:item.text},{text:valid.text});
}
export function removeClaim(vault: VaultData, caseId: string, claimId: string): VaultData { return changeCase(vault,caseId,'claim_removed', item=>({...item,claims:item.claims.filter(row=>row.id!==claimId)})); }
export function renameCase(vault: VaultData, caseId: string, label: string, purpose?: CaseNotebook['purpose']): VaultData {
  const valid = createCase(emptyForValidation(vault),label,'sensitive').cases[0]!.label;
  return changeCase(vault,caseId,'case_renamed',item=>({...item,label:valid,purpose:purpose ?? item.purpose}),{label:vault.cases.find(row=>row.id===caseId)?.label ?? ''},{label:valid});
}
function emptyForValidation(vault: VaultData): VaultData { return {...vault,cases:[],history:[]}; }
export function deleteCase(vault: VaultData, caseId: string): VaultData {
  const item=vault.cases.find(row=>row.id===caseId);
  if (!item?.archivedAt) throw new Error('ARCHIVE_BEFORE_DELETE');
  return {...vault, updatedAt:now(), cases:vault.cases.filter(row=>row.id!==caseId),history:vault.history.filter(row=>row.caseId!==caseId),verificationQueue:vault.verificationQueue.filter(row=>row.caseId!==caseId),rechecks:vault.rechecks.filter(row=>row.caseId!==caseId)};
}
export function moveReceipt(vault: VaultData, fromCaseId: string, receiptId: string, toCaseId: string): VaultData {
  if (fromCaseId===toCaseId) throw new Error('SAME_CASE');
  const from=vault.cases.find(row=>row.id===fromCaseId); const to=vault.cases.find(row=>row.id===toCaseId);
  const receipt=from?.receipts.find(row=>row.id===receiptId);
  if (!receipt || !to || to.archivedAt) throw new Error('MOVE_TARGET_INVALID');
  const claims=from!.claims.filter(row=>row.sourceReceiptIds.includes(receiptId)).map(row=>({...row,sourceReceiptIds:[receiptId]}));
  let next=removeReceipt(vault,fromCaseId,receiptId);
  next=changeCase(next,fromCaseId,'receipt_moved',item=>({...item,claims:item.claims.filter(row=>!claims.some(claim=>claim.id===row.id))}));
  return changeCase(next,toCaseId,'receipt_moved',item=>({...item,receipts:[...item.receipts,{...receipt,relatedReceiptId:undefined}],claims:[...item.claims,...claims]}));
}
export function recordExport(vault: VaultData, caseId: string, exportKind: HistoryEntry['exportKind']): VaultData {
 return appendHistory(vault,{caseId,type:'case_exported',exportKind,revision:vault.cases.find(row=>row.id===caseId)!.revision,summary:'기록을 파일이나 글로 꺼냈어요.'});
}
export function claimPacketUsage(vault: VaultData, at=new Date()): number { return vault.history.filter(row=>row.type==='case_exported' && row.exportKind==='claim_packet' && new Date(row.at).getFullYear()===at.getFullYear() && new Date(row.at).getMonth()===at.getMonth()).length; }
